import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Menu, X } from "lucide-react";
import { LOGO_URL } from "../../lib/brand";
import { SERVICES } from "../../data/services";

const links = [
  { name: "About", to: "/about" },
  { name: "Insights", to: "/insights" },
  { name: "Contact", to: "/contact" },
];

const navClass = ({ isActive }) =>
  `font-body text-sm tracking-wide transition-colors ${isActive ? "text-summit-gold-dark" : "text-summit-charcoal hover:text-summit-gold-dark"}`;

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServices, setMobileServices] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  const servicesActive = location.pathname.startsWith("/services");

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 border-b ${
        scrolled || open ? "bg-summit-bg/95 backdrop-blur-md border-summit-border py-3" : "bg-transparent border-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3" aria-label="Summit Transformation Group home">
          <img src={LOGO_URL} alt="Summit Transformation Group" className="h-14 md:h-16 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-9" aria-label="Primary">
          <NavLink to="/" end className={navClass} data-testid="nav-link-home">Home</NavLink>

          <div
            className="relative"
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <button
              data-testid="nav-services-trigger"
              onClick={() => setServicesOpen((v) => !v)}
              aria-expanded={servicesOpen}
              className={`flex items-center gap-1.5 font-body text-sm tracking-wide transition-colors ${
                servicesActive ? "text-summit-gold-dark" : "text-summit-charcoal hover:text-summit-gold-dark"
              }`}
            >
              Services <ChevronDown size={14} className={`transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {servicesOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.18 }}
                  className="absolute left-1/2 -translate-x-1/2 top-full pt-4"
                >
                  <div data-testid="nav-services-menu" className="w-[340px] bg-summit-bg border border-summit-border shadow-[0_24px_60px_-20px_rgba(44,62,53,0.25)] p-2">
                    {SERVICES.map((s) => (
                      <Link
                        key={s.slug}
                        to={`/services/${s.slug}`}
                        data-testid={`nav-service-${s.slug}`}
                        className="block px-4 py-3 hover:bg-summit-sand transition-colors group"
                      >
                        <span className="flex items-center justify-between">
                          <span className="font-heading text-lg text-summit-charcoal group-hover:text-summit-gold-dark transition-colors">{s.navLabel}</span>
                          {s.lead && <span className="text-[10px] uppercase tracking-[0.18em] font-body font-semibold text-summit-gold-dark">Lead</span>}
                        </span>
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {links.map((l) => (
            <NavLink key={l.to} to={l.to} className={navClass} data-testid={`nav-link-${l.name.toLowerCase()}`}>
              {l.name}
            </NavLink>
          ))}

          <Link
            to="/contact"
            data-testid="nav-cta-btn"
            className="font-body text-sm font-medium tracking-wide bg-summit-forest text-white px-6 py-3 hover:bg-summit-forest-dark transition-colors"
          >
            Schedule a Strategy Conversation
          </Link>
        </nav>

        <button data-testid="mobile-menu-btn" className="lg:hidden text-summit-charcoal" onClick={() => setOpen((v) => !v)} aria-label="Toggle menu">
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden overflow-hidden bg-summit-bg border-t border-summit-border"
          >
            <div className="px-6 py-6 flex flex-col gap-1 max-h-[calc(100vh-90px)] overflow-y-auto">
              <Link to="/" className="font-heading text-2xl text-summit-charcoal py-2" data-testid="mobile-nav-home">Home</Link>
              <button
                onClick={() => setMobileServices((v) => !v)}
                className="flex items-center justify-between font-heading text-2xl text-summit-charcoal py-2 w-full text-left"
                data-testid="mobile-nav-services-toggle"
              >
                Services <ChevronDown size={18} className={`transition-transform ${mobileServices ? "rotate-180" : ""}`} />
              </button>
              {mobileServices && (
                <div className="pl-4 border-l border-summit-gold/50 ml-1 mb-2 flex flex-col">
                  {SERVICES.map((s) => (
                    <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`mobile-nav-service-${s.slug}`} className="font-body text-summit-text-muted py-2.5">
                      {s.navLabel}
                    </Link>
                  ))}
                </div>
              )}
              {links.map((l) => (
                <Link key={l.to} to={l.to} className="font-heading text-2xl text-summit-charcoal py-2" data-testid={`mobile-nav-${l.name.toLowerCase()}`}>
                  {l.name}
                </Link>
              ))}
              <Link to="/contact" data-testid="mobile-nav-cta" className="mt-4 text-center font-body text-sm font-medium tracking-wide bg-summit-forest text-white px-6 py-4">
                Schedule a Strategy Conversation
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
